import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import UndoIcon from '@mui/icons-material/Undo';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const API_BASE = 'https://system.sales-management.cloud/api/admin/daily-report.php';
const BASE_URL = 'https://system.sales-management.cloud';

// ファイルURLを完全なURLに変換
const getFullUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path; // 既に完全なURLの場合
    return BASE_URL + path;
};

function AdminDailyReport() {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [selectedReport, setSelectedReport] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [filePreview, setFilePreview] = useState({});
    const [filters, setFilters] = useState({
        user_id: '',
        status: '',
        start_date: '',
        end_date: ''
    });

    // ステータス表示設定
    const statusConfig = {
        pending: { label: '未承認', color: 'bg-yellow-100 text-yellow-800', icon: PendingIcon },
        approved: { label: '承認済', color: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
        rejected: { label: '却下', color: 'bg-red-100 text-red-800', icon: CancelIcon }
    };

    // 日報一覧を取得
    const fetchReports = React.useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const token = Cookies.get('auth_token');
            if (!token) {
                setError('認証情報が見つかりません');
                return;
            }

            const params = new URLSearchParams();
            if (filters.user_id) params.append('user_id', filters.user_id);
            if (filters.status) params.append('status', filters.status);
            if (filters.start_date) params.append('start_date', filters.start_date);
            if (filters.end_date) params.append('end_date', filters.end_date);

            const response = await fetch(`${API_BASE}?${params.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();
            
            console.log('API Response:', data);
            if (data.status === 'success') {
                setReports(data.data.reports || []);
            } else {
                setError(data.message || '日報の取得に失敗しました');
            }
        } catch (err) {
            console.error('Fetch error:', err);
            setError('通信エラーが発生しました');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    // 日報詳細を取得
    const fetchReportDetail = async (reportId) => {
        try {
            const token = Cookies.get('auth_token');
            if (!token) {
                setError('認証情報が見つかりません');
                return;
            }

            const response = await fetch(`${API_BASE}?id=${reportId}&include_files=1`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();
            
            if (data.status === 'success') {
                // APIレスポンスの二重ネスト構造に対応
                const report = data.data;
                if (report.daily_report_content && report.daily_report_content.daily_report_content) {
                    // ネストされた構造をアンラップ
                    report.daily_report_content = report.daily_report_content.daily_report_content;
                }
                setSelectedReport(report);
                setShowDetailModal(true);
            } else {
                setError(data.message || '日報詳細の取得に失敗しました');
            }
        } catch (err) {
            console.error('Fetch detail error:', err);
            setError('通信エラーが発生しました');
        }
    };

    // ステータス更新
    const updateReportStatus = async (reportId, newStatus) => {
        try {
            const token = Cookies.get('auth_token');
            if (!token) {
                setError('認証情報が見つかりません');
                return;
            }

            const response = await fetch(API_BASE, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    report_id: reportId,
                    new_status: newStatus
                })
            });

            const data = await response.json();
            
            if (data.status === 'success') {
                setSuccess(`ステータスを「${statusConfig[newStatus].label}」に更新しました`);
                setShowDetailModal(false);
                setSelectedReport(null);

                // データを再取得
                fetchReports();
            } else {
                setError(data.message || 'ステータスの更新に失敗しました');
            }
        } catch (err) {
            console.error('Update error:', err);
            setError('通信エラーが発生しました');
        }
    };

    useEffect(() => {
        fetchReports();
    }, [filters, fetchReports]);

    // 日付フォーマット
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-6">
            <div className="max-w-7xl mx-auto">
                {/* ヘッダー */}
                <div className="mb-6">
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">業務日報管理</h1>
                    <p className="text-sm text-gray-600">業務委託者の日報を確認・管理します</p>
                </div>

                {/* 成功・エラーメッセージ */}
                {success && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                        <p className="text-sm text-green-800">{success}</p>
                    </div>
                )}
                {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-800">{error}</p>
                    </div>
                )}

{/* フィルター */}
                <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">フィルター</h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">ユーザーID</label>
                            <input
                                type="number"
                                value={filters.user_id}
                                onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
                                placeholder="ユーザーID"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">ステータス</label>
                            <select
                                value={filters.status}
                                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">すべて</option>
                                <option value="pending">未承認</option>
                                <option value="approved">承認済</option>
                                <option value="rejected">却下</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">開始日 (YYYY-MM-DD)</label>
                            <input
                                type="date"
                                value={filters.start_date}
                                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">終了日 (YYYY-MM-DD)</label>
                            <input
                                type="date"
                                value={filters.end_date}
                                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    <div className="mt-4 flex gap-2">
                        <button
                            onClick={() => fetchReports()}
                            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                        >
                            検索
                        </button>
                        <button
                            onClick={() => {
                                setFilters({ user_id: '', status: '', start_date: '', end_date: '' });
                                setSuccess('');
                                setError('');
                            }}
                            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium"
                        >
                            クリア
                        </button>
                    </div>
                </div>

                {/* 一覧表示 */}
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center text-gray-500">読み込み中...</div>
                    ) : reports.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">日報がありません</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ユーザー</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">作業日</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ステータス</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {reports.map((report) => {
                                        const StatusIcon = statusConfig[report.approval_status]?.icon || DescriptionIcon;
                                        return (
                                            <tr key={report.report_id} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                    {report.report_id}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <PersonIcon className="text-gray-400 mr-2" fontSize="small" />
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-900">{report.user_name}</div>
                                                            <div className="text-xs text-gray-500">ID: {report.user_id}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                    {formatDate(report.work_date)}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig[report.approval_status]?.color}`}>
                                                        <StatusIcon fontSize="small" className="mr-1" />
                                                        {statusConfig[report.approval_status]?.label}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                    <button
                                                        onClick={() => fetchReportDetail(report.report_id)}
                                                        className="text-blue-600 hover:text-blue-800 font-medium"
                                                    >
                                                        詳細
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* 詳細モーダル */}
                {showDetailModal && selectedReport && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
                                <h2 className="text-xl font-bold text-gray-900">日報詳細</h2>
                                <button
                                    onClick={() => {
                                        setShowDetailModal(false);
                                        setSelectedReport(null);
                                        setFilePreview({});
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="p-6 space-y-6">
                                {/* 基本情報 */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">日報ID</label>
                                        <p className="text-lg font-semibold text-gray-900">{selectedReport.report_id}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">ユーザー</label>
                                        <p className="text-lg font-semibold text-gray-900">{selectedReport.user_name}</p>
                                        <p className="text-sm text-gray-500">ID: {selectedReport.user_id}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">作業日</label>
                                        <p className="text-lg font-semibold text-gray-900">{formatDate(selectedReport.work_date)}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">ステータス</label>
                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusConfig[selectedReport.approval_status]?.color}`}>
                                            {statusConfig[selectedReport.approval_status]?.label}
                                        </span>
                                    </div>
                                </div>

                                {/* 遅刻チェック（詳細表示） */}
                                {selectedReport.is_late && (
                                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-1">
                                        <p className="text-sm font-semibold text-orange-800">⚠ 遅延があります</p>
                                        {selectedReport.late_reason && (
                                            <p className="text-sm text-orange-900">理由: {selectedReport.late_reason}</p>
                                        )}
                                        {selectedReport.late_minutes != null && (
                                            <p className="text-sm text-orange-900">遅延時間: {selectedReport.late_minutes}分</p>
                                        )}
                                    </div>
                                )}

                                {/* 動的フィールド */}
                                {selectedReport.daily_report_content && Object.keys(selectedReport.daily_report_content).length > 0 && (
                                    <div className="border-t pt-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">カスタムフィールド</h3>
                                        <div className="space-y-3">
                                            {Object.entries(selectedReport.daily_report_content).map(([key, value]) => (
                                                <div key={key}>
                                                    <label className="block text-sm font-medium text-gray-500 mb-1">{key}</label>
                                                    <p className="text-gray-900 bg-gray-50 p-3 rounded-md whitespace-pre-wrap">{value || '-'}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* メモ */}
                                {selectedReport.memo && (
                                    <div className="border-t pt-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">メモ</h3>
                                        <p className="text-gray-900 bg-gray-50 p-3 rounded-md whitespace-pre-wrap">{selectedReport.memo}</p>
                                    </div>
                                )}

                                {/* 添付ファイル */}
                                {selectedReport.files && selectedReport.files.length > 0 && (
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-3">添付ファイル</h3>
                                        <div className="space-y-4">
                                            {selectedReport.files.map((file) => {
                                                const isImage = file.mime?.startsWith('image/');
                                                const isPdf = file.mime === 'application/pdf';
                                                const isPreviewable = isImage || isPdf;
                                                const isHidden = filePreview[file.file_id] === false; // 明示的に非表示にされた場合のみ非表示

                                                return (
                                                    <div key={file.file_id} className="border border-gray-200 rounded-lg overflow-hidden">
                                                        <div className="flex items-center justify-between p-3 bg-gray-50">
                                                            <div>
                                                                <p className="font-medium text-gray-900">{file.file_name}</p>
                                                                <p className="text-sm text-gray-500">{file.mime} • {(file.size / 1024).toFixed(1)} KB</p>
                                                            </div>
                                                            <div className="flex gap-2">
                                                                {isPreviewable && (
                                                                    <button
                                                                        onClick={() => setFilePreview({
                                                                            ...filePreview,
                                                                            [file.file_id]: !isHidden
                                                                        })}
                                                                        className="flex items-center px-3 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm font-medium"
                                                                    >
                                                                        {isHidden ? (
                                                                            <>
                                                                                <VisibilityIcon className="mr-1" fontSize="small" />
                                                                                表示
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <VisibilityOffIcon className="mr-1" fontSize="small" />
                                                                                非表示
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                )}
                                                                <a
                                                                    href={getFullUrl(file.url)}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                                                                >
                                                                    ダウンロード
                                                                </a>
                                                            </div>
                                                        </div>

                                                        {/* プレビュー表示 */}
                                                        {!isHidden && isPreviewable && (
                                                            <div className="bg-white p-4 border-t border-gray-200">
                                                                {isImage && (
                                                                    <img
                                                                        src={getFullUrl(file.url)}
                                                                        alt={file.file_name}
                                                                        className="max-w-full max-h-96 mx-auto rounded-lg"
                                                                    />
                                                                )}
                                                                {isPdf && (
                                                                    <iframe
                                                                        src={getFullUrl(file.url)}
                                                                        title={file.file_name}
                                                                        className="w-full h-96 border border-gray-300 rounded-lg"
                                                                    />
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* 承認者情報 */}
                                {selectedReport.approval_status === 'approved' && selectedReport.approved_by && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                        <p className="text-sm text-gray-600">承認者</p>
                                        <p className="text-lg font-semibold text-green-800">{selectedReport.approved_by}</p>
                                        <p className="text-sm text-gray-600 mt-1">{formatDate(selectedReport.approved_at)}</p>
                                    </div>
                                )}

                                {/* ステータス変更ボタン */}
                                {selectedReport.approval_status === 'pending' && (
                                    <div className="border-t pt-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">ステータス変更</h3>
                                        <div className="flex flex-wrap gap-3">
                                            <button
                                                onClick={() => updateReportStatus(selectedReport.report_id, 'approved')}
                                                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                                            >
                                                <CheckCircleIcon className="mr-2" fontSize="small" />
                                                承認
                                            </button>
                                            <button
                                                onClick={() => updateReportStatus(selectedReport.report_id, 'rejected')}
                                                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                                            >
                                                <CancelIcon className="mr-2" fontSize="small" />
                                                却下
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {selectedReport.approval_status === 'approved' && (
                                    <div className="border-t pt-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">ステータス変更</h3>
                                        <div className="flex flex-wrap gap-3">
                                            <button
                                                onClick={() => updateReportStatus(selectedReport.report_id, 'pending')}
                                                className="flex items-center px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
                                            >
                                                <UndoIcon className="mr-2" fontSize="small" />
                                                差戻
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {selectedReport.approval_status === 'rejected' && (
                                    <div className="border-t pt-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">ステータス変更</h3>
                                        <div className="flex flex-wrap gap-3">
                                            <button
                                                onClick={() => updateReportStatus(selectedReport.report_id, 'pending')}
                                                className="flex items-center px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
                                            >
                                                <UndoIcon className="mr-2" fontSize="small" />
                                                再検討
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AdminDailyReport;
